import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, ChevronDown } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useVendorProfile } from "../../services/vendorProfileService";
import LogoutModal from "./LogoutModal";
import Button from "./Button";
import { useVendorVerification } from "../../hooks/useVendorVerification";
import NotificationBell from "./NotificationBell";
import VendorBroadcastModal from "../vendor/VendorBroadcastModal";
import ThemeToggle from "./ThemeToggle";

const VendorHeader = () => {
  const { user, logout } = useAuth();
  const { toggleVendorMobileSidebar } = useSidebar();
  const navigate = useNavigate();
  const { isVendorVerified, isVerificationPending } = useVendorVerification();
  const { data: profileResp } = useVendorProfile();
  const profile = profileResp?.data || user?.vendor_profile || null;
  const businessName = profile?.business_name || null;
  const businessLogoUrl =
    profile?.logo_url || profile?.brand_settings?.logo_url || null;

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [businessLogoFailed, setBusinessLogoFailed] = useState(false);
  const [broadcastModal, setBroadcastModal] = useState({ isOpen: false, notification: null });
  const dropdownRef = useRef(null);

  useEffect(() => {
    setBusinessLogoFailed(false);
  }, [businessLogoUrl]);

  useEffect(() => {
    const handleShowBroadcast = (e) => {
      if (e.detail?.notification) {
        setBroadcastModal({ isOpen: true, notification: e.detail.notification });
      }
    };
    window.addEventListener("vendor:showBroadcastModal", handleShowBroadcast);
    return () => window.removeEventListener("vendor:showBroadcastModal", handleShowBroadcast);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowLogoutModal(false);
    setShowUserDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initial = (businessName || user?.name || "V").charAt(0).toUpperCase();

  return (
    <header className="fixed top-0 left-0 right-0 z-[10000] bg-white/95 dark:bg-[#111827]/95 backdrop-blur-md border-b border-gray-200/80 dark:border-gray-800/90 transition-colors">
      <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            type="button"
            onClick={toggleVendorMobileSidebar}
            className="lg:hidden p-2 -ml-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <Link
            to="/vendor"
            className="flex items-center gap-2.5 min-w-0 max-w-[16rem] lg:max-w-xs rounded-lg px-1 py-1 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors group"
            title={businessName || "Vendor dashboard"}
          >
            {businessLogoUrl && !businessLogoFailed ? (
              <img
                src={businessLogoUrl}
                alt={businessName || "Business logo"}
                className="w-9 h-9 rounded-lg object-cover border border-gray-200 dark:border-gray-700 bg-white shrink-0"
                onError={() => setBusinessLogoFailed(true)}
              />
            ) : (
              <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-sm shrink-0">
                <span className="text-white font-semibold text-sm">{initial}</span>
              </span>
            )}
            <div className="hidden sm:flex flex-col leading-none min-w-0">
              <span className="text-base font-semibold tracking-tight text-gray-900 dark:text-white truncate group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">
                {businessName || user?.name || "Vendor"}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-gray-400 mt-0.5">
                Vendor
              </span>
            </div>
          </Link>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <ThemeToggle size="sm" />

          {!isVendorVerified && (
            <div className="relative hidden sm:block">
              {!isVerificationPending && (
                <div className="absolute inset-x-0.5 inset-y-0 bg-primary-600 rounded-full animate-ping opacity-20" />
              )}
              <Button
                onClick={() => {
                  if (!isVerificationPending) navigate("/vendor/verification");
                }}
                variant={isVerificationPending ? "secondary" : "primary"}
                size="sm"
                className={`!rounded-full !shadow-md relative z-10 ${
                  isVerificationPending ? "cursor-default" : ""
                }`}
                disabled={isVerificationPending}
              >
                {isVerificationPending ? "Pending approval" : "Verify identity"}
              </Button>
            </div>
          )}

          <NotificationBell
            onBroadcastClick={(notification) => {
              setBroadcastModal({ isOpen: true, notification });
            }}
          />

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center gap-1.5 sm:gap-2 rounded-full pl-0.5 pr-1.5 sm:pr-2 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              aria-expanded={showUserDropdown}
              aria-haspopup="menu"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center ring-2 ring-white dark:ring-gray-900 shadow-sm">
                <span className="text-white font-semibold text-xs">{initial}</span>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden sm:block" />
            </button>

            {showUserDropdown && (
              <div
                className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-xl shadow-xl py-1.5 z-[10001] border border-gray-200 dark:border-gray-700 overflow-hidden"
                role="menu"
              >
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                  {businessName ? (
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {businessName}
                    </p>
                  ) : null}
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {user?.name || "Vendor"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {user?.email || ""}
                  </p>
                </div>
                <Link
                  to="/vendor/account"
                  onClick={() => setShowUserDropdown(false)}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  role="menuitem"
                >
                  Account settings
                </Link>
                <div className="px-2 py-1 border-b border-gray-100 dark:border-gray-800 sm:hidden">
                  <ThemeToggle showLabel className="w-full justify-start px-2" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserDropdown(false);
                    setShowLogoutModal(true);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                  role="menuitem"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />

      <VendorBroadcastModal
        isOpen={broadcastModal.isOpen}
        onClose={() => setBroadcastModal({ isOpen: false, notification: null })}
        notification={broadcastModal.notification}
      />
    </header>
  );
};

export default VendorHeader;
